const common = {
  'appName': 'Appwin',
  'retry': 'Retry',
  'cancel': 'Cancel',
  'close': 'Close',
  'save': 'Save',
  'delete': 'Delete',
  'confirm': 'Confirm',
  'back': 'Back',
  'next': 'Next',
  'search': 'Search',
  'loading': 'Loading…',
  'noData': 'No data',
  'enabled': 'Enabled',
  'disabled': 'Disabled',
  'yes': 'Yes',
  'no': 'No',
  'copy': 'Copy',
  'copied': 'Copied',
  'learnMore': 'Learn more',
  'seeAll': 'See all',
  'optional': 'optional',
  'required': 'required',
  'settings': 'Settings',
  'signIn': 'Sign in',
  'signOut': 'Sign out',
  'language': 'Language',
  'version': 'Version',
  'size': 'Size',
  'updated': 'Updated',
  'actions': 'Actions',
  'status': 'Status',
  'reason': 'Reason',
  'date': 'Date',
  'unknown': 'Unknown',
  'none': 'None',
} as const;

const nav = {
  'catalog': 'Catalogue',
  'updates': 'Updates',
  'installed': 'Installed',
  'settings': 'Settings',
  'open': 'Open Appwin',
  'checkUpdates': 'Check for updates',
  'quit': 'Quit',
} as const;

const catalog = {
  'title': 'App catalogue',
  'subtitle': 'Discover, install and keep Windows apps up to date.',
  'searchPlaceholder': 'Search apps',
  'categories': 'Categories',
  'allCategories': 'All categories',
  'empty': 'Nothing found',
  'emptyHint': 'Try a different search or category.',
  'resultsCount': '{count} apps',
  'loadMore': 'Load more',
  'externalSource': 'External source',
  'externalSourceHint':
    'This app is imported from winget. Files are downloaded from the publisher.',
  'updatedRecently': 'Recently updated',
  'popular': 'Popular',
  'new': 'New',
  'unverified': 'Unverified publisher',
} as const;

const app = {
  'install': 'Install',
  'installed': 'Installed',
  'update': 'Update',
  'reinstall': 'Reinstall',
  'open': 'Open',
  'details': 'Details',
  'screenshots': 'Screenshots',
  'changelog': 'What’s new',
  'versions': 'Versions',
  'releaseNotes': 'Release notes',
  'homepage': 'Homepage',
  'license': 'License',
  'developer': 'Developer',
  'category': 'Category',
  'requirements': 'Requirements',
  'arch': 'Architecture',
  'scope_per_user': 'This user',
  'scope_machine': 'All users',
  'scope_portable': 'Portable',
  'signature_valid': 'Digitally signed',
  'signature_none': 'Not signed',
  'signature_invalid': 'Invalid signature',
  'signature_unverified': 'Signature not verified',
  'autoUpdate': 'Automatic updates',
  'manualUpdateOnly': 'Manual updates only',
  'notFound': 'App not found',
} as const;

const install = {
  'planTitle': 'Install {name} {version}',
  'planSource': 'Source',
  'planSize': 'Download size',
  'planRights': 'Permissions',
  'planRights_per_user': 'Installs for the current user only.',
  'planRights_machine': 'Requires administrator confirmation (UAC).',
  'planRights_portable': 'Extracts to a portable folder; no system changes.',
  'planSignature': 'Publisher signature',
  'planContinue': 'Continue',
  'downloading': 'Downloading…',
  'verifying': 'Verifying file integrity…',
  'verifyingSignature': 'Verifying signature…',
  'installing': 'Installing…',
  'verifyingInstall': 'Checking installation…',
  'rebootRequired': 'A restart is required to finish.',
  'restartLater': 'Later',
  'restartNow': 'Restart now',
  'success': 'Installed successfully',
  'launch': 'Launch',
  'whyFailed': 'Why did it fail?',
  'errorHash': 'Downloaded file does not match the published SHA-256. Installation stopped.',
  'errorSignature': 'The catalogue feed signature is invalid. Installation stopped.',
  'errorNetwork': 'Network error while downloading. Check your connection and retry.',
  'errorElevation': 'Administrator confirmation was denied.',
  'errorTimeout': 'The installer did not finish in time.',
  'errorGeneric': 'Installation failed ({code}).',
  'manualInstallRequired': 'This app can only be installed manually.',
  'manualInstallHint': 'Open the download page and run the installer yourself.',
  'openDownloadPage': 'Open download page',
  'cancelled': 'Installation cancelled',
} as const;

const updates = {
  'title': 'Updates',
  'allUpToDate': 'Everything is up to date',
  'allUpToDateHint': 'Appwin checks for updates every four hours.',
  'updateAll': 'Update all ({count})',
  'available': '{count} updates available',
  'skipVersion': 'Skip this version',
  'pinVersion': 'Keep current version',
  'unpin': 'Allow updates again',
  'whyNotUpdated': 'Why isn’t this updated automatically?',
  'reasonNoSilent': 'The publisher does not provide unattended install arguments.',
  'reasonIneligible': 'This release is excluded from automatic updates.',
  'reasonUnknownMatch': 'Appwin is not sure this installed app is the same one.',
  'reasonPinned': 'You chose to keep the current version.',
  'reasonSkipped': 'You skipped this version.',
  'rollback': 'Roll back to {version}',
  'rollbackUnavailable': 'The previous version is no longer in the cache.',
  'checking': 'Checking for updates…',
  'lastChecked': 'Last checked {time}',
  'neverChecked': 'Never checked',
} as const;

const installed = {
  'title': 'Installed apps',
  'subtitle': 'Apps detected on this PC.',
  'scanning': 'Scanning installed apps…',
  'source_catalog': 'From Appwin',
  'source_registry': 'System registry',
  'source_msix': 'Microsoft Store package',
  'confidence_exact': 'Exact match',
  'confidence_probable': 'Probable match',
  'confidence_unknown': 'Match uncertain',
  'notThisApp': 'This is not the same app',
  'notThisAppSent': 'Thanks — we will review this match.',
  'empty': 'No installed apps detected',
  'filterPlaceholder': 'Filter installed apps',
} as const;

const settings = {
  'title': 'Settings',
  'autoUpdate': 'Automatically update apps',
  'autoUpdateHint': 'Checks every four hours when Appwin is running.',
  'startWithWindows': 'Start Appwin at sign-in',
  'startWithWindowsHint': 'Runs in the system tray in the background.',
  'keepInTray': 'Keep running in the tray after closing the window',
  'language': 'Interface language',
  'telemetry': 'Send anonymous install reports',
  'telemetryHint':
    'Helps us spot broken installers. Includes app id, outcome and an error code — never the app contents, file names or your identity.',
  'proxy': 'Proxy',
  'proxyHint': 'Optional. Format: http://host:port or socks5://host:port',
  'cacheLimit': 'Download cache limit',
  'clearCache': 'Clear cache',
  'cacheCleared': 'Cache cleared',
  'about': 'About Appwin',
  'clientVersion': 'Client version',
  'apiVersion': 'API version',
  'diagnostics': 'Open logs folder',
  'resetClientId': 'Reset anonymous client id',
} as const;

const errors = {
  'generic': 'Something went wrong. Please try again.',
  'network': 'Cannot reach the Appwin service.',
  'offline': 'You are offline. Some features are unavailable.',
  'rateLimited': 'Too many requests. Please wait a moment.',
  'apiOutdated': 'Please update Appwin to continue.',
  'apiOutdatedHint': 'This version of the app is too old for the current service.',
  'updateAppwin': 'Update Appwin',
  'invalidDeepLink': 'The link could not be opened because it is not a valid Appwin link.',
  'appYanked': 'This version was withdrawn by the publisher or moderators.',
  'appDelisted': 'This app is no longer available.',
} as const;

const web = {
  'downloadClient': 'Download Appwin for Windows',
  'downloadClientHint': 'Free. Windows 10 and 11, x64 and ARM64.',
  'openInAppwin': 'Open in Appwin',
  'openInAppwinHint': 'If Appwin is installed, the app opens right away.',
  'getAppwinFirst': 'Appwin isn’t installed yet',
  'getAppwinFirstHint': 'Download Appwin to install apps in one click.',
  'installTitle': 'Install {name}',
  'notFoundTitle': 'Page not found',
  'notFoundHint': 'The app you are looking for may have been removed.',
  'description': 'A Windows app catalogue with verified installers and automatic updates.',
  'skipToContent': 'Skip to content',
  'searchCatalog': 'Search the catalogue',
  'directDownload': 'Download installer',
  'directDownloadHint': 'Opens the publisher\'s own download. Appwin does not host the file.',
  'versionsAvailable': 'Available versions: {count}',
} as const;

const developer = {
  'title': 'Developer portal',
  'signInGithub': 'Continue with GitHub',
  'signInEmail': 'Continue with email',
  'emailLabel': 'Email address',
  'sendCode': 'Send code',
  'codeLabel': 'Six-digit code',
  'codeSent': 'We sent a code to {email}.',
  'verify': 'Verify',
  'myApps': 'My apps',
  'newApp': 'New app',
  'appIdLabel': 'App id',
  'appIdHint': 'Permanent. Format: your-nickname/app-name. Cannot be changed later.',
  'nameLabel': 'Display name',
  'categoryLabel': 'Category',
  'summaryLabel': 'Short summary',
  'descriptionLabel': 'Full description',
  'homepageLabel': 'Homepage',
  'licenseLabel': 'License (SPDX)',
  'uploadRelease': 'Upload a release',
  'uploadVersion': 'Version',
  'uploadFile': 'Installer file',
  'uploadKind': 'Package type',
  'uploadScope': 'Install scope',
  'silentArgs': 'Silent install arguments',
  'silentArgsHint': 'Required for EXE and MSI so updates can run unattended.',
  'upgradeArgs': 'Upgrade arguments',
  'submitForReview': 'Submit for review',
  'submitted': 'Submitted. Reviews usually take up to two business days.',
  'apiKeys': 'API keys',
  'newApiKey': 'New API key',
  'apiKeyOnce': 'Copy this key now. It will not be shown again.',
  'revoke': 'Revoke',
  'stats': 'Statistics',
  'claimWinget': 'Claim a winget package',
  'claimHint': 'Import an existing winget entry and manage it as your own app.',
  'releaseInReview': 'In review',
  'releasePublished': 'Published',
  'releaseRejected': 'Rejected',
  'releaseYanked': 'Withdrawn',
} as const;

const admin = {
  'title': 'Appwin admin',
  'moderation': 'Moderation queue',
  'approve': 'Approve',
  'reject': 'Reject',
  'yank': 'Withdraw',
  'checks': 'Automated checks',
  'sha256': 'SHA-256',
  'av': 'Antivirus',
  'authenticode': 'Code signature',
  'imports': 'Winget import',
  'runImport': 'Run import',
  'stats': 'Dashboard',
  'audit': 'Audit log',
  'installs': 'Installs',
  'updates': 'Updates',
  'successRate': 'Success rate',
  'noTelemetry': 'No data — install reports are disabled or none received.',
  'topApps': 'Most installed apps',
  'topErrors': 'Most frequent errors',
  'pending': 'Awaiting review',
  'delist': 'Delist app',
  'relist': 'Relist app',
  'hashMismatches': 'Hash mismatches',
  'lastRun': 'Last import',
} as const;

export const en = {
  common,
  nav,
  catalog,
  app,
  install,
  updates,
  installed,
  settings,
  errors,
  web,
  developer,
  admin,
};

export type EnMessages = {
  [NS in keyof typeof en]: { [K in keyof (typeof en)[NS]]: string };
};
